import '@/theme/partials/_button.scss';

import { Capacitor } from '@capacitor/core';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonImg,
  IonInfiniteScroll,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRow,
  IonSpinner,
  IonText,
  IonToolbar,
} from '@ionic/react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { closeOutline } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import Download from '@/assets/icons/download.svg';
import Info from '@/assets/icons/info.svg';
import ArrowLeft from '@/assets/icons/keyboard-arrow-left-black.svg';
import emptyStateImg from '@/assets/imgs/empty-reporting.svg';
import Banner from '@/components/common/Banner';
import { BannerType } from '@/components/common/Banner/Banner';
import { ROUTES_PATH } from '@/constants';
import { SettlementsAPIData, SettlementsDetail } from '@/interfaces/ReportingInterface/ReportingInterface';
import { saveSettlementDetail } from '@/redux/reporting/reporting';
import { getSettlementDetail } from '@/redux/reporting/reportingAPI';
import { fileOpenerService } from '@/services/fileOpener';
import { RootState } from '@/store';
import { addToBOM, downloadCsv, exportToCsv } from '@/utils/csvExporter';
import { getTodayDate } from '@/utils/dateHelper';
import { transformAmount, transformDate } from '@/utils/settlementsHelper';

import styles from './SettlementDetail.module.scss';

const SettlementDetail = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const [settlementId, setSettlementId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filterValues, setFilterValues] = useState<any>();
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [openInfoModal, setOpenInfoModal] = useState<boolean>(false);
  const [modalData, setModalData] = useState<SettlementsDetail | null>(null);

  const contentRef = useRef<HTMLIonContentElement | null>(null);

  const settlementDetail = useSelector(
    (state: RootState) => state.settlement.data.settlementDetail,
  );

  const exportTable = () => {
    if (settlementDetail && settlementDetail.list?.length > 0) {
      const settlementList: any[] = [];
      settlementDetail.list.map((settlement: SettlementsDetail) => {
        if (settlement.order_id) {
          settlementList.push({
            Fecha: settlement.payment_date,
            'ID Pedido': settlement.order_id,
            'ID Propuesta': settlement.proposal_id,
            'UUID Propuesta': settlement.proposal_uuid,
            Tipo: settlement.type,
            Importe: `${settlement.financed_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} €`,
            Deducido: `${settlement.deduced_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} €`,
            Transferido: `${settlement.net_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} €`,
          });
        } else {
          settlementList.push({
            Fecha: settlement.payment_date,
            'ID Propuesta': settlement.proposal_id,
            'UUID Propuesta': settlement.proposal_uuid,
            Tipo: settlement.type,
            Importe: `${settlement.financed_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} €`,
            Deducido: `${settlement.deduced_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} €`,
            Transferido: `${settlement.net_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} €`,
          });
        }
      });
      if (Capacitor.isNativePlatform()) {
        const settlementData = exportToCsv(settlementList);
        if (settlementData && settlementDetail.list?.length > 0) {
          const csv = addToBOM(settlementData);
          const paymentDate = settlementDetail?.list[0]?.payment_date;
          fileOpenerService.openSettlementCsv(csv, paymentDate && dayjs(paymentDate).format('DD-MM-YYYY'));
        }
      } else {
        downloadCsv(settlementList);
      }
    }
  };

  const pageReturn = () => {
    if (settlementDetail && settlementDetail.list?.length > 0) {
      const date = settlementDetail.list[0]?.payment_date.split('-');
      date.pop();
      const dateParam = date.join('-');
      history.push(`${ROUTES_PATH.MONTH_SETTLEMENT}/${dateParam}`);
    }
  };

  const searchNext = async ($event: CustomEvent<void>) => {
    setLoadingData(true);
    const newOffset = currentPage + 30;
    const newFilterValues = {
      ...filterValues,
      offset: newOffset,
    };
    setCurrentPage(newOffset);
    setFilterValues(newFilterValues);
    const response = await getSettlementDetail(settlementId, newFilterValues);
    if (settlementDetail && settlementDetail.list && settlementDetail.list?.length > 0) {
      const newsettlementDetail = {
        list: [...settlementDetail.list, ...response.data.settlements_detail],
        more_items: response.metadata?.paging.more_items,
      };
      dispatch(saveSettlementDetail(newsettlementDetail));
    }
    setLoadingData(false);
    const target = $event.target as HTMLIonInfiniteScrollElement;
    target.complete();
    setLoadingData(false);
  };

  const retrieveSettlementDetail = async (id: string) => {
    setLoadingData(true);
    const todayDate = getTodayDate();
    const params: SettlementsAPIData = {
      offset: 0,
      limit: 30,
      from_date: '&from_date=1990-01-01',
      to_date: `&to_date=${todayDate}`,
    };
    const response = await getSettlementDetail(id, params);
    const detailRetrieved = {
      list: response.data.settlements_detail,
      more_items: response.metadata?.paging.more_items,
    };
    const newFilterValues = {
      ...params,
      from_date: `&from_date=${response.data.settlements_detail[0]?.payment_date}`,
      to_date: `&to_date=${response.data.settlements_detail[0]?.payment_date}`,
    };
    dispatch(saveSettlementDetail(detailRetrieved));
    setFilterValues(newFilterValues);
    setLoadingData(false);
    setLoadingPage(false);
  };

  useEffect(() => {
    const pathname = location.pathname.split('/');
    const settlementIdParam = pathname[pathname.length - 1];
    if (settlementDetail && settlementDetail.list && settlementDetail.list?.length > 0) {
      setFilterValues({
        offset: 0 + Math.floor(settlementDetail.list.length / 30),
        limit: 30,
        from_date: `&from_date=${settlementDetail?.list[0]?.payment_date}`,
        to_date: `&to_date=${settlementDetail?.list[0]?.payment_date}`,
      });
    } else {
      setLoadingPage(true);
      retrieveSettlementDetail(settlementIdParam);
    }
    setSettlementId(settlementIdParam);
  }, []);

  return (
    <IonPage className={styles.settlementDetailPage}>
      <IonContent ref={contentRef} scrollEvents className={styles.reportingContent}>
        <Banner type={BannerType.REPORTING} />
        <div className={styles.settlementTableContent}>
          <IonButton className={styles.reportingReturnBtn} onClick={pageReturn}>
            <IonImg src={ArrowLeft} className={styles.reportingReturnIcon} />
            Volver a liquidaciones mes
          </IonButton>
          <div className={styles.reportingTableWrapper}>
            <div className={styles.reportingMainCard}>
              {loadingPage && (
                <div className={styles.reportingLoadingContainer}>
                  <div className={styles.customSpinner}>
                    <IonSpinner
                      color="danger"
                      name="bubbles"
                      className={styles.scrollSpinner}
                    />
                    <span className={styles.spinnerText}>Cargando...</span>
                  </div>
                </div>
              )}
              <div className={classNames(styles.titleWrapper, styles.reportingSettlementTitle)}>
                <div className={styles.settlementTitleWrapper}>
                  <p className={styles.titleText}>
                    Liquidación
                    {' '}
                    {settlementDetail?.list && settlementDetail.list?.length > 0
                      && transformDate(settlementDetail?.list[0].payment_date)}
                  </p>
                  <IonButtons className={styles.reportingDownloadBtnWrapper}>
                    <IonButton className={classNames('secondaryButton', styles.reportingDetailBtn, styles.reportingDownloadBtn)} onClick={exportTable}>
                      <IonText>Descargar listado</IonText>
                      <IonImg src={Download} />
                    </IonButton>
                  </IonButtons>
                </div>
                <hr className={styles.reportingDetailLine} />
              </div>
              {!loadingPage
                && (
                  <>
                    <IonList className={styles.settlementListDesktop}>
                      <IonGrid className={styles.reportingGridWrapper}>
                        <IonRow className={styles.reportingTableRow}>
                          <IonCol className={styles.paymentDateTableCol}>
                            <IonRow>
                              <IonLabel
                                className={classNames(styles.generalTableTitle, styles.sortAvailable)}
                              >
                                Fecha
                              </IonLabel>
                            </IonRow>
                          </IonCol>
                          {settlementDetail?.list && settlementDetail?.list.length > 0
                            && settlementDetail.list[0].order_id
                            && (
                              <IonCol className={styles.orderTableCol}>
                                <IonRow>
                                  <IonLabel
                                    className={styles.generalTableTitle}
                                  >
                                    ID Pedido
                                  </IonLabel>
                                </IonRow>
                              </IonCol>
                            )}
                          <IonCol className={styles.proposalIdTableCol}>
                            <IonRow>
                              <IonLabel
                                className={styles.generalTableTitle}
                              >
                                ID Propuesta
                              </IonLabel>
                            </IonRow>
                          </IonCol>
                          <IonCol className={styles.proposalUuidTableCol}>
                            <IonRow>
                              <IonLabel
                                className={styles.generalTableTitle}
                              >
                                Uuid Prop.
                              </IonLabel>
                            </IonRow>
                          </IonCol>
                          <IonCol className={styles.typeTableCol}>
                            <IonRow>
                              <IonLabel
                                className={styles.generalTableTitle}
                              >
                                Tipo
                              </IonLabel>
                            </IonRow>
                          </IonCol>
                          <IonCol className={styles.amountTableCol}>
                            <IonRow>
                              <IonLabel
                                className={styles.generalTableTitle}
                              >
                                Importe
                              </IonLabel>
                            </IonRow>
                          </IonCol>
                          <IonCol className={styles.deductedTableCol}>
                            <IonRow>
                              <IonLabel
                                className={styles.generalTableTitle}
                              >
                                Deducido
                              </IonLabel>
                            </IonRow>
                          </IonCol>
                          <IonCol className={styles.transferredTableCol}>
                            <IonRow>
                              <IonLabel
                                className={styles.generalTableTitle}
                              >
                                Transferido
                              </IonLabel>
                            </IonRow>
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                      <IonGrid>
                        {settlementDetail?.list.map((settlement: SettlementsDetail) => (
                          <IonRow key={settlementDetail.list.indexOf(settlement)} className={styles.reportingTableRowData}>
                            <IonCol className={styles.paymentDateTableCol}>
                              <IonLabel>
                                {transformDate(settlement.payment_date)}
                              </IonLabel>
                            </IonCol>
                            {settlement.order_id
                              && (
                                <IonCol className={styles.orderTableCol}>
                                  <IonLabel>
                                    {settlement.order_id}
                                  </IonLabel>
                                </IonCol>
                              )}
                            <IonCol className={styles.proposalIdTableCol}>
                              <IonLabel>
                                {settlement.proposal_id}
                              </IonLabel>
                            </IonCol>
                            <IonCol className={styles.proposalUuidTableCol}>
                              <IonLabel>
                                {settlement.proposal_uuid}
                              </IonLabel>
                            </IonCol>
                            <IonCol className={classNames(styles.typeTableCol, styles.reportingTableCol)}>
                              <IonRow className={styles.typeTableContent}>
                                <IonLabel>
                                  {settlement.type}
                                </IonLabel>
                              </IonRow>
                            </IonCol>
                            <IonCol className={styles.amountTableCol}>
                              <IonRow className={styles.transferredTableContent}>
                                <IonLabel>
                                  {transformAmount(settlement.financed_amount)}
                                </IonLabel>
                              </IonRow>
                            </IonCol>
                            <IonCol className={styles.deductedTableCol}>
                              <IonRow className={styles.transferredTableContent}>
                                <IonLabel>
                                  {transformAmount(settlement?.deduced_amount ? settlement.deduced_amount : 0)}
                                </IonLabel>

                              </IonRow>
                            </IonCol>
                            <IonCol className={styles.transferredTableCol}>
                              <IonRow>
                                <IonLabel>
                                  {transformAmount(settlement.net_amount)}
                                </IonLabel>
                              </IonRow>
                            </IonCol>
                          </IonRow>
                        ))}
                      </IonGrid>
                    </IonList>
                    <IonInfiniteScroll
                      id="settlement-detail-scroll"
                      threshold="25px"
                      disabled={!settlementDetail?.more_items}
                      onIonInfinite={(e: CustomEvent<void>) => searchNext(e)}
                      className={styles.scrollWrapper}
                    >
                      {(settlementDetail?.more_items
                        || loadingData
                      ) && (
                      <IonSpinner
                        color="danger"
                        name="bubbles"
                        className={styles.settlementScrollSpinner}
                      />
                      )}
                    </IonInfiniteScroll>
                  </>
                )}
            </div>
          </div>
        </div>
        <div className={styles.settlementTableResponsive}>
          {loadingPage && (
            <div className={styles.reportingLoadingContainerResponsive}>
              <div className={styles.customSpinner}>
                <IonSpinner
                  color="danger"
                  name="bubbles"
                  className={styles.settlementScrollSpinner}
                />
                <span className={styles.spinnerText}>Cargando...</span>
              </div>
            </div>
          )}
          <div className={styles.settlementTableContainer}>
            <div className={styles.reportingTitleWrapper}>
              <p className={styles.titleText}>Reporting</p>
            </div>
            <div className={styles.reportingTitleResponsive}>
              <IonButton className={styles.reportingReturnBtn} onClick={pageReturn}>
                <IonImg src={ArrowLeft} className={styles.reportingReturnIcon} />
              </IonButton>
              <p className={classNames(styles.titleText, styles.reportingSettlementTitle)}>
                Liquidación
                {' '}
                -
                {' '}
                {settlementDetail && settlementDetail.list?.length > 0 && transformDate(settlementDetail.list[0]?.payment_date)}
              </p>
            </div>
            {!loadingPage && (
              <div className={styles.settlementTableWrapperResponsive}>
                <IonList className={styles.settlementListResponsive}>
                  <IonGrid className={styles.reportingGridWrapper}>
                    {settlementDetail?.list.map((detail: SettlementsDetail) => (
                      <IonRow key={settlementDetail.list.indexOf(detail)} className={styles.reportingTableMobileCard}>
                        <IonRow
                          className={styles.reportingMobileCardContent}
                          onClick={() => {
                            setModalData(detail);
                            setOpenInfoModal(true);
                          }}
                        >
                          <IonCol>
                            <IonRow className={styles.reportingTableMobileDate}>
                              Id Carrito:
                              {' '}
                              {detail.cart_id}
                            </IonRow>
                            <IonRow className={classNames(styles.reportingTableMobileAmount, detail.net_amount < 0 ? styles.reportingMobileAmountNegative : styles.reportingMobileAmountPositive)}>
                              Transferido:
                              {' '}
                              {transformAmount(detail.net_amount)}
                            </IonRow>
                            <IonRow className={styles.reportingTableMobileSubtitle}>{detail.order_id}</IonRow>
                          </IonCol>
                          <IonCol className={styles.reportingTableMobileBtn}>
                            <IonLabel>
                              <IonImg className={styles.liquidationDetailInfoMobile} src={Info} />
                            </IonLabel>
                          </IonCol>
                        </IonRow>
                        <IonRow>
                          <hr className={styles.reportingDetailLine} />
                        </IonRow>
                      </IonRow>
                    ))}
                  </IonGrid>
                </IonList>
                <IonInfiniteScroll
                  id="settlement-detail-scroll-mobile"
                  threshold="0px"
                  disabled={!settlementDetail?.more_items}
                  onIonInfinite={(e: CustomEvent<void>) => searchNext(e)}
                  className={styles.scrollWrapper}
                >
                  {(settlementDetail?.more_items
                    || loadingData
                  ) && (
                  <div className={styles.settlementScrollSpinner}>
                    <IonSpinner
                      color="danger"
                      name="bubbles"
                      className={styles.scrollSpinner}
                    />
                  </div>
                  )}
                </IonInfiniteScroll>
              </div>
            )}
          </div>
          {!loadingPage
            ? (
              <div className={styles.reportingDownloadBtnWrapper}>
                <IonButtons>
                  <IonButton className={classNames('secondaryButton', styles.reportingDetailBtn, styles.reportingDownloadBtn)} onClick={exportTable}>
                    <IonText>Descargar listado</IonText>
                    <IonImg src={Download} />
                  </IonButton>
                </IonButtons>
              </div>
            )
            : (
              <div className={styles.reportingTableEmptySpace}>
                <div>
                  <img src={emptyStateImg} alt="no liquidations" />
                </div>
              </div>
            )}
          <IonModal
            isOpen={openInfoModal}
            onDidDismiss={() => {
              setOpenInfoModal(false);
              setModalData(null);
            }}
            className={styles.reportingInfoModal}
            initialBreakpoint={1}
            breakpoints={[0, 1]}
          >
            <IonHeader>
              <IonToolbar className={styles.reportingHeaderModal}>
                <IonButtons slot="end">
                  <IonButton
                    className={styles.reportingCloseModal}
                    onClick={() => {
                      setOpenInfoModal(false);
                      setModalData(null);
                    }}
                  >
                    <IonIcon className={styles.modalCloseBtn} md={closeOutline} />
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent className={styles.reportingModalContent}>
              <div className={styles.modalContentWrapper}>
                <IonRow className={styles.reportingModalTitleWrapper}>
                  <div className={styles.reportingModalTitle}>Detalle transferido</div>
                </IonRow>
                <div className={styles.reportingModalBox}>
                  <IonRow className={styles.reportingModalRow}>
                    <IonLabel className={styles.modalTransferredTextMobile}>IdCarrito</IonLabel>
                    <IonLabel
                      className={styles.modalTransferredAmountMobile}
                    >
                      {modalData?.cart_id}
                    </IonLabel>
                  </IonRow>
                  <IonRow className={styles.reportingModalRow}>
                    <IonLabel className={styles.modalTransferredTextMobile}>IdPedido</IonLabel>
                    <IonLabel
                      className={styles.modalTransferredAmountMobile}
                    >
                      {modalData?.order_id}
                    </IonLabel>
                  </IonRow>
                  <IonRow className={styles.reportingModalRow}>
                    <IonLabel className={styles.modalTransferredTextMobile}>IdPropuesta</IonLabel>
                    <IonLabel
                      className={styles.modalTransferredAmountMobile}
                    >
                      {modalData?.proposal_id}
                    </IonLabel>
                  </IonRow>
                  <IonRow className={styles.reportingModalRow}>
                    <IonLabel className={styles.modalTransferredTextMobile}>UuidPropuesta</IonLabel>
                    <IonLabel
                      className={styles.modalTransferredAmountMobile}
                    >
                      {modalData?.proposal_uuid}
                    </IonLabel>
                  </IonRow>
                  <IonRow className={styles.reportingModalRow}>
                    <IonLabel className={styles.modalTransferredTextMobile}>Tipo</IonLabel>
                    <IonLabel className={styles.modalTransferredHighlightMobile}>{modalData?.type}</IonLabel>
                  </IonRow>
                  <IonRow className={styles.reportingModalRow}>
                    <IonLabel className={styles.modalTransferredTextMobile}>Importe transferido</IonLabel>
                    <IonLabel className={styles.modalTransferredHighlightMobile}>{modalData && transformAmount(modalData?.net_amount)}</IonLabel>
                  </IonRow>
                  <IonRow className={classNames(styles.reportingModalRow, styles.reportingModalSubmenu)}>
                    <IonLabel className={styles.modalTransferredSubmenuText}>Importe</IonLabel>
                    <IonLabel className={styles.modalTransferredSubmenuAmount}>{modalData && transformAmount(modalData?.financed_amount)}</IonLabel>
                  </IonRow>
                  <IonRow className={classNames(styles.reportingModalRow, styles.reportingModalSubmenu)}>
                    <IonLabel className={styles.modalTransferredSubmenuText}>Deducido</IonLabel>
                    <IonLabel className={styles.modalTransferredSubmenuAmount}>{modalData && transformAmount(modalData?.deduced_amount)}</IonLabel>
                  </IonRow>
                </div>
              </div>
            </IonContent>
          </IonModal>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SettlementDetail;
